import React from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Skeleton,
  Chip,
  Typography,
  Tooltip,
  Button,
} from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import ActionBadge from './ActionBadge';

const CLICKABLE_ENTITY_TYPES = ['ENTERPRISE', 'EMPLOYER', 'CONTRACT', 'REPORT', 'TASK'];

const ENTITY_LABELS = {
  ENTERPRISE: 'Entreprise',
  CONTRACT: 'Contrat',
  EMPLOYER: 'Employeur',
  REQUEST: 'Demande',
  REPORT: 'Rapport',
  TASK: 'Tâche',
  USER: 'Utilisateur',
  AUTH: 'Auth',
};

function formatDate(isoStr) {
  if (!isoStr) return '—';
  return new Date(isoStr).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function ActivityTable({
  rows = [],
  loading,
  page,
  totalElements,
  totalPages,
  rowsPerPage = 20,
  onPageChange,
  onEntityClick,
}) {
  const skeletonRows = Array(rowsPerPage).fill(null);

  return (
    <Paper elevation={2}>
      <TableContainer>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Utilisateur</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Action</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Entité</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Objet</TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 220 }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Date / Heure</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Adresse IP</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? skeletonRows.map((_, i) => (
                  <TableRow key={i}>
                    {Array(7)
                      .fill(null)
                      .map((__, j) => (
                        <TableCell key={j}>
                          <Skeleton variant="text" />
                        </TableCell>
                      ))}
                  </TableRow>
                ))
              : rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      {row.user
                        ? `${row.user.firstName} ${row.user.lastName}`
                        : <Typography variant="caption" color="text.secondary">Système</Typography>}
                    </TableCell>
                    <TableCell>
                      <ActionBadge actionType={row.actionType} />
                    </TableCell>
                    <TableCell>
                      {row.entityType && (
                        <Chip
                          label={ENTITY_LABELS[row.entityType] ?? row.entityType}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {CLICKABLE_ENTITY_TYPES.includes(row.entityType) && row.entityId && onEntityClick ? (
                        <Button
                          size="small"
                          variant="text"
                          startIcon={<OpenInNew fontSize="small" />}
                          onClick={() => onEntityClick(row)}
                          sx={{ textTransform: 'none', p: 0 }}
                        >
                          {row.entityName ?? '—'}
                        </Button>
                      ) : (
                        <Typography variant="body2">{row.entityName ?? '—'}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={row.description ?? ''} placement="top">
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 300,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {row.description ?? '—'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                        {formatDate(row.timestamp)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {row.ipAddress ?? '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalElements ?? -1}
        page={page}
        onPageChange={(_, newPage) => onPageChange(newPage)}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[rowsPerPage]}
        labelDisplayedRows={({ from, to, count }) =>
          `${from}–${to} sur ${count !== -1 ? count : '?'}`
        }
        labelRowsPerPage="Lignes par page"
      />
    </Paper>
  );
}
